/** Vogel (1979) sunflower packing and the arithmetic that makes it break. */

export const PHI = (1 + Math.sqrt(5)) / 2;
/** Fraction of a turn: 1 − 1/φ = 1/φ² ≈ 0.381966 */
export const GOLDEN_TURN = 1 - 1 / PHI;
/** ≈ 137.50776405003785° */
export const GOLDEN_ANGLE_DEG = 360 * GOLDEN_TURN;

export type Seed = {
  n: number;
  theta: number;
  r: number;
  x: number;
  y: number;
};

export type Convergent = { p: number; q: number };

export function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

export function degToRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

export function wrapTurn(turn: number): number {
  const t = turn % 1;
  return t < 0 ? t + 1 : t;
}

export function circularDiffDeg(a: number, b: number): number {
  const d = Math.abs(a - b) % 360;
  return Math.min(d, 360 - d);
}

export function goldenErrorDeg(deg: number): number {
  return circularDiffDeg(deg, GOLDEN_ANGLE_DEG);
}

export function vogelSeed(n: number, divergenceRad: number, c = 1): Seed {
  const theta = n * divergenceRad;
  const r = c * Math.sqrt(n);
  return {
    n,
    theta,
    r,
    x: r * Math.cos(theta),
    y: r * Math.sin(theta),
  };
}

export function packHead(count: number, divergenceDeg: number, c = 1): Seed[] {
  const nSeeds = Math.max(0, Math.floor(count));
  const rad = degToRad(divergenceDeg);
  const seeds: Seed[] = [];
  for (let n = 0; n < nSeeds; n++) seeds.push(vogelSeed(n, rad, c));
  return seeds;
}

export function continuedFraction(x: number, maxTerms = 16, eps = 1e-12): number[] {
  const a: number[] = [];
  let v = x;
  for (let i = 0; i < maxTerms; i++) {
    if (!Number.isFinite(v)) break;
    const ai = Math.floor(v + 1e-15);
    a.push(ai);
    const frac = v - ai;
    if (Math.abs(frac) < eps) break;
    v = 1 / frac;
    if (Math.abs(v) > 1e12) break;
  }
  return a;
}

export function convergents(a: number[]): Convergent[] {
  const out: Convergent[] = [];
  let p0 = 0;
  let q0 = 1;
  let p1 = 1;
  let q1 = 0;
  for (const ai of a) {
    const p = ai * p1 + p0;
    const q = ai * q1 + q0;
    out.push({ p, q });
    p0 = p1;
    q0 = q1;
    p1 = p;
    q1 = q;
  }
  return out;
}

export function turnConvergents(divergenceDeg: number): Convergent[] {
  return convergents(continuedFraction(wrapTurn(divergenceDeg / 360)));
}

/**
 * The two most visible spiral families (parastichies) for this packing.
 * Denominators of the last two convergents whose q is below ~√N.
 */
export function visibleParastichies(
  divergenceDeg: number,
  seedCount: number,
): { a: number; b: number } {
  const conv = turnConvergents(divergenceDeg).filter((c) => c.q > 0);
  if (conv.length === 0) return { a: 1, b: 2 };
  const cap = Math.max(5, Math.sqrt(Math.max(1, seedCount)) * 1.7);
  let i = 0;
  while (i + 1 < conv.length && conv[i + 1]!.q <= cap) i++;
  const b = conv[i]!;
  const a = i > 0 ? conv[i - 1]! : { p: 0, q: Math.max(1, b.q - 1) };
  return { a: Math.max(1, a.q), b: Math.max(a.q + 1, b.q) };
}

/**
 * Low-order rationals (q ≤ 12) whose turn is within 0.2° of p/q.
 * Golden is the limit of Fibonacci ratios, so those must not count as spokes.
 */
export function namedSpokes(divergenceDeg: number): { q: number; errorDeg: number } | null {
  const turn = wrapTurn(divergenceDeg / 360);
  let best: { q: number; errorDeg: number } | null = null;
  for (let q = 2; q <= 12; q++) {
    const p = Math.round(turn * q);
    const errorDeg = Math.abs(turn - p / q) * 360;
    if (errorDeg >= 0.2) continue;
    if (!best || errorDeg < best.errorDeg || (errorDeg === best.errorDeg && q < best.q)) {
      best = { q, errorDeg };
    }
  }
  return best;
}

/**
 * Radial collapse: a named rational, or a 0.1°-scale miss of the golden turn.
 * Fibonacci approximations of φ do not count — that is the packed head.
 */
export function spokeHint(
  divergenceDeg: number,
  seedCount: number,
): { q: number; errorDeg: number } | null {
  const named = namedSpokes(divergenceDeg);
  if (named) return named;
  const err = goldenErrorDeg(divergenceDeg);
  if (err >= 0.085 && err < 2.5) {
    const { b } = visibleParastichies(divergenceDeg, seedCount);
    return { q: b, errorDeg: err };
  }
  return null;
}

export type PackingKind = "golden" | "near-golden" | "spokes" | "other";

export function packingKind(divergenceDeg: number, seedCount: number): PackingKind {
  const err = goldenErrorDeg(divergenceDeg);
  if (err < 0.002) return "golden";
  if (err < 0.085) return "near-golden";
  if (spokeHint(divergenceDeg, seedCount)) return "spokes";
  return "other";
}

export const PRESETS: { id: string; deg: number; label: string; hint: string }[] = [
  { id: "golden", deg: GOLDEN_ANGLE_DEG, label: "Golden", hint: "Most irrational turn. Tight packing." },
  { id: "137.5", deg: 137.5, label: "137.5°", hint: "A tenth of a degree from golden. Still packed." },
  { id: "137.4", deg: 137.4, label: "137.4°", hint: "Nudge −0.1°. Spirals collapse into spokes." },
  { id: "137.6", deg: 137.6, label: "137.6°", hint: "Nudge +0.1°. The other way into spokes." },
  { id: "144", deg: 144, label: "144°", hint: "360° × 2/5. Five-fold spokes." },
  { id: "120", deg: 120, label: "120°", hint: "One third of a turn. Three ranks." },
  { id: "90", deg: 90, label: "90°", hint: "Quarter turn. A cross of seeds." },
  { id: "180", deg: 180, label: "180°", hint: "Half turn. A single line." },
];
